import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { Alert, BrandMark, Button, Field, Input } from '@mt/ui';
import { Staff } from '@mt/ui/notation';
import { CheckCircle2, Eye, EyeOff, MailCheck } from 'lucide-react';
import { BRAND } from '../config/brand.ts';
import { authRedirect } from '../config/env.ts';
import { supabase } from '../lib/supabase.ts';
import { useAuth } from './AuthProvider.tsx';
import { authErrorMessage, PASSWORD_MIN } from './authErrors.ts';
import { safeNext } from './guards.tsx';
import { countGuestVisit } from '../lib/guestUsage.ts';

function AuthLayout({ title, subtitle, children, footer }: { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="grid min-h-dvh bg-paper lg:grid-cols-[1.05fr_1fr]">
      <section className="relative hidden overflow-hidden bg-hero text-white lg:flex lg:flex-col lg:justify-between lg:p-12">
        <Link to="/" className="flex w-fit items-center gap-3 rounded-lg" aria-label={`${BRAND.name} home`}>
          <BrandMark className="size-10" />
          <span className="font-display text-2xl font-bold">{BRAND.name}</span>
        </Link>
        <div className="max-w-md" aria-hidden="true">
          <p className="font-display text-4xl leading-tight font-bold">{BRAND.tagline}</p>
          <div className="mt-8 rounded-2xl bg-surface p-4 text-ink shadow-raised">
            <Staff clef="treble" notes={['E4', 'G4', 'B4', 'D5', 'F5']} label="" size="md" />
          </div>
          <p className="mt-4 text-white/75">Daily practice, clear explanations, and classes with your teacher.</p>
        </div>
        <p className="text-sm text-white/60" aria-hidden="true">For students and teachers</p>
      </section>
      <main className="flex flex-col justify-center px-4 py-10 sm:px-10">
        <div className="mx-auto w-full max-w-sm">
          <Link to="/" className="mb-8 flex w-fit items-center gap-2.5 rounded-lg lg:hidden" aria-label={`${BRAND.name} home`}>
            <BrandMark />
            <span className="font-display text-xl font-bold">{BRAND.name}</span>
          </Link>
          <h1 className="text-3xl font-bold">{title}</h1>
          {subtitle && <p className="mt-2 text-muted">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-8 text-center text-sm text-muted">{footer}</div>}
        </div>
      </main>
    </div>
  );
}

function PasswordInput({ id, value, onChange, autoComplete, invalid }: { id: string; value: string; onChange: (v: string) => void; autoComplete: string; invalid?: boolean }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        id={id}
        type={visible ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required
        aria-invalid={invalid || undefined}
        className="pr-12"
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        className="absolute inset-y-0 right-0 grid w-12 place-items-center text-muted hover:text-ink"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
      >
        {visible ? <EyeOff className="size-5" aria-hidden="true" /> : <Eye className="size-5" aria-hidden="true" />}
      </button>
    </div>
  );
}

export function SignInPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { expired } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const next = params.get('next');

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await supabase().auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (err) return setError(authErrorMessage(err));
    navigate(safeNext(next) ?? '/', { replace: true });
  }

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to keep your streak going."
      footer={
        <>
          New here?{' '}
          <Link to="/signup" className="font-semibold text-primary underline-offset-4 hover:underline">
            Create an account
          </Link>
          <span className="mt-3 block">
            <Link to="/try" className="font-semibold text-primary underline-offset-4 hover:underline">
              Practice without an account
            </Link>
          </span>
        </>
      }
    >
      {(expired || params.get('expired')) && (
        <div className="mb-5">
          <Alert tone="brass" title="Your session ended">
            Sign in again to continue. Answers you already gave are saved.
          </Alert>
        </div>
      )}
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label="Email" htmlFor="email">
          <Input id="email" type="email" inputMode="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password" htmlFor="password">
          <PasswordInput id="password" value={password} onChange={setPassword} autoComplete="current-password" invalid={Boolean(error)} />
        </Field>
        {error && <Alert tone="rose">{error}</Alert>}
        <Button type="submit" size="lg" block loading={busy} disabled={!email || !password}>
          Sign in
        </Button>
        <Link to="/forgot-password" className="text-center text-sm font-semibold text-primary underline-offset-4 hover:underline">
          Forgot your password?
        </Link>
      </form>
    </AuthLayout>
  );
}

export function SignUpPage() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [checkEmail, setCheckEmail] = useState(false);
  const tooShort = password.length > 0 && password.length < PASSWORD_MIN;
  useEffect(() => countGuestVisit('signup_page_visit'), []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password.length < PASSWORD_MIN) return setError(`Use at least ${PASSWORD_MIN} characters for your password.`);
    if (!name.trim()) return setError('Enter the name others will see.');
    setBusy(true);
    setError(null);
    const { data, error: err } = await supabase().auth.signUp({
      email: email.trim(),
      password,
      options: { data: { display_name: name.trim().slice(0, 40) }, emailRedirectTo: authRedirect('/onboarding') },
    });
    setBusy(false);
    if (err) return setError(authErrorMessage(err));
    if (!data.session) return setCheckEmail(true);
    navigate('/onboarding', { replace: true });
  }

  if (checkEmail) {
    return (
      <AuthLayout title="Check your email" subtitle={<>We sent a confirmation link to <strong className="text-ink">{email}</strong>.</>}>
        <Alert tone="sky" icon={<MailCheck className="size-5" aria-hidden="true" />}>
          Open the link on this device to finish creating your account. It can take a minute to arrive — check spam if you don’t see it.
        </Alert>
        <Link to="/signin" className="mt-6 block text-center font-semibold text-primary underline-offset-4 hover:underline">
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Free for students and teachers."
      footer={
        <>
          Already have an account?{' '}
          <Link to="/signin" className="font-semibold text-primary underline-offset-4 hover:underline">
            Sign in
          </Link>
          <span className="mt-3 block">
            <Link to="/try" className="font-semibold text-primary underline-offset-4 hover:underline">
              Practice without an account
            </Link>
          </span>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label="Display name" htmlFor="name" hint="Shown to your teacher and on class leaderboards.">
          <Input id="name" autoComplete="nickname" maxLength={40} required value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Email" htmlFor="email">
          <Input id="email" type="email" inputMode="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password" htmlFor="password" hint={`At least ${PASSWORD_MIN} characters.`} error={tooShort ? `At least ${PASSWORD_MIN} characters.` : null}>
          <PasswordInput id="password" value={password} onChange={setPassword} autoComplete="new-password" invalid={tooShort} />
        </Field>
        {error && <Alert tone="rose">{error}</Alert>}
        <Button type="submit" size="lg" block loading={busy} disabled={!email || !password || !name}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await supabase().auth.resetPasswordForEmail(email.trim(), { redirectTo: authRedirect('/reset-password') });
    setBusy(false);
    if (err) return setError(authErrorMessage(err));
    setSent(true);
  }

  return (
    <AuthLayout title="Reset your password" subtitle="We’ll email you a link to choose a new password." footer={<Link to="/signin" className="font-semibold text-primary underline-offset-4 hover:underline">Back to sign in</Link>}>
      {sent ? (
        <Alert tone="fern" icon={<CheckCircle2 className="size-5" aria-hidden="true" />} title="Check your email">
          If an account exists for {email}, a reset link is on its way. The link works once and expires after an hour.
        </Alert>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
          <Field label="Email" htmlFor="email">
            <Input id="email" type="email" inputMode="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          {error && <Alert tone="rose">{error}</Alert>}
          <Button type="submit" size="lg" block loading={busy} disabled={!email}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { session, restoring } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const linkError = new URLSearchParams(location.hash.slice(1)).get('error_description');

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password.length < PASSWORD_MIN) return setError(`Use at least ${PASSWORD_MIN} characters.`);
    if (password !== confirm) return setError('The passwords don’t match.');
    setBusy(true);
    setError(null);
    const { error: err } = await supabase().auth.updateUser({ password });
    setBusy(false);
    if (err) return setError(authErrorMessage(err));
    navigate('/', { replace: true });
  }

  if (restoring) return <AuthLayout title="Reset your password">Checking your link…</AuthLayout>;
  if (!session) {
    return (
      <AuthLayout title="Link expired" subtitle={linkError ?? 'This reset link is invalid or has already been used.'}>
        <Link to="/forgot-password" className="font-semibold text-primary underline-offset-4 hover:underline">
          Request a new link
        </Link>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label="New password" htmlFor="new-password" hint={`At least ${PASSWORD_MIN} characters.`}>
          <PasswordInput id="new-password" value={password} onChange={setPassword} autoComplete="new-password" />
        </Field>
        <Field label="Confirm new password" htmlFor="confirm-password">
          <PasswordInput id="confirm-password" value={confirm} onChange={setConfirm} autoComplete="new-password" invalid={confirm.length > 0 && confirm !== password} />
        </Field>
        {error && <Alert tone="rose">{error}</Alert>}
        <Button type="submit" size="lg" block loading={busy}>
          Save password
        </Button>
      </form>
    </AuthLayout>
  );
}
